import React from 'react';
import { useLocation } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import UsersManagementSection from './UsersManagementSection';
import FileTypesSection from './FileTypesSection';
import GeneralSettingsSection from './GeneralSettingsSection';
import CardTemplatesSection from './CardTemplatesSection';

/**
 * Main view component for the system settings pages
 * Handles routing between different admin sections
 * 
 * @param {Object} props - Component props
 * @param {Array} props.allowedFileTypes - List of allowed file types
 * @param {Array} props.users - List of system users
 * @param {boolean} props.loading - General loading state
 * @param {boolean} props.usersLoading - Users loading state
 * @param {string} props.error - Error message if any
 * @param {Object} props.newFileType - New file type being added
 * @param {Object} props.generalSettings - System general settings
 * @param {Object} props.selectedUser - Currently selected user
 * @param {Array} props.selectedUserApplications - Applications of selected user
 * @param {boolean} props.applicationsLoading - Applications loading state
 * @param {Function} props.handleGeneralSettingsUpdate - Function to update general settings
 * @param {Function} props.onInputChange - Input change handler
 * @param {Function} props.onAddFileType - Function to add a new file type
 * @param {Function} props.onToggleFileType - Function to toggle file type status
 * @param {Function} props.onDeleteFileType - Function to delete a file type
 * @param {Function} props.onUpdateFileType - Function to update a file type
 * @param {Function} props.onUserSelect - Function to handle user selection
 * @param {Function} props.onModalClose - Function to close modals
 * @param {boolean} props.saveSuccess - Whether save was successful
 */
export default function SystemSettingsView({
  allowedFileTypes,
  users,
  loading,
  usersLoading,
  error,
  newFileType,
  generalSettings,
  selectedUser,
  selectedUserApplications,
  applicationsLoading,
  handleGeneralSettingsUpdate,
  onInputChange,
  onAddFileType,
  onToggleFileType,
  onDeleteFileType,
  onUpdateFileType,
  onUserSelect,
  onModalClose,
  saveSuccess
}) {
  const location = useLocation();
  const currentPath = location.pathname;

  // Determine what content to show based on the path
  const showUsers = currentPath === '/admin/users';
  const showGeneralSettings = currentPath === '/admin/system-settings';
  const showCardTemplates = currentPath === '/admin/card-templates';
  
  // Page title and description based on current view
  let pageTitle = "System Settings";
  let pageDescription = "Manage and edit system settings and configuration";
  
  if (showUsers) {
    pageTitle = "User Management";
    pageDescription = "View and manage system users";
  } else if (showCardTemplates) {
    pageTitle = "Card Templates";
    pageDescription = "Manage deployment process card templates";
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 text-gray-100">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-blue-400 text-left">{pageTitle}</h1>
        <p className="text-gray-400 text-left">{pageDescription}</p>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Admin Sidebar */}
        <AdminSidebar />
        
        {/* Main Content */}
        <main className="flex-1">
          {/* Show different content based on current path */}
          {showUsers && (
            <UsersManagementSection 
              users={users} 
              loading={usersLoading}
              selectedUser={selectedUser}
              selectedUserApplications={selectedUserApplications}
              applicationsLoading={applicationsLoading}
              onUserSelect={onUserSelect}
              onModalClose={onModalClose}
            />
          )}
          
          {showGeneralSettings && (
            <>
              {/* General Settings Section */}
              <GeneralSettingsSection 
                settings={generalSettings}
                onUpdateSettings={handleGeneralSettingsUpdate}
                loading={loading}
                error={error}
                saveSuccess={saveSuccess}
              />
              
              {/* File Types Section */}
              <FileTypesSection 
                allowedFileTypes={allowedFileTypes}
                loading={loading}
                error={error}
                newFileType={newFileType}
                onInputChange={onInputChange}
                onAddFileType={onAddFileType}
                onToggleFileType={onToggleFileType}
                onDeleteFileType={onDeleteFileType}
                saveSuccess={saveSuccess}
              />
            </>
          )}

          {/* Card Templates Section */}
          {showCardTemplates && (
            <CardTemplatesSection />
          )}
        </main>
      </div>
    </div>
  );
} 